import { forwardRef } from 'react';
import { cn } from '../../lib/utils';
import Spinner from './Spinner';

/**
 * Button component - Modern Sleek Design System
 *
 * Variants:
 * - primary: Teal background, white text (main CTAs)
 * - secondary: Subtle border, transparent bg (secondary actions)
 * - outline: Cream border on dark backgrounds
 * - ghost: Transparent, text only
 * - danger: Red background for destructive actions
 * - accent: Amber/gold gradient for special actions
 *
 * Sizes: sm, md, lg
 */

const variants = {
  primary: [
    'bg-primary text-white',
    'shadow-sm hover:shadow-glow-primary',
    'hover:bg-primary-dark',
    'focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-midnight',
  ].join(' '),
  secondary: [
    'bg-transparent text-cream border border-cream/30',
    'hover:bg-cream/10 hover:border-cream/50',
    'focus:ring-2 focus:ring-cream/30 focus:ring-offset-2 focus:ring-offset-midnight',
  ].join(' '),
  outline: [
    'bg-transparent text-cream border border-cream/30',
    'hover:border-cream/60 hover:bg-cream/5',
    'focus:ring-2 focus:ring-cream/50 focus:ring-offset-2 focus:ring-offset-midnight',
  ].join(' '),
  'outline-dark': [
    'bg-transparent text-midnight border border-midnight/30',
    'hover:border-midnight/60 hover:bg-midnight/5',
    'focus:ring-2 focus:ring-midnight/50 focus:ring-offset-2 focus:ring-offset-cream',
  ].join(' '),
  ghost: [
    'bg-transparent text-cream/70',
    'hover:text-cream hover:bg-cream/5',
    'focus:ring-2 focus:ring-cream/30 focus:ring-offset-2 focus:ring-offset-midnight',
  ].join(' '),
  'ghost-dark': [
    'bg-transparent text-midnight/70',
    'hover:text-midnight hover:bg-midnight/5',
    'focus:ring-2 focus:ring-midnight/30 focus:ring-offset-2 focus:ring-offset-cream',
  ].join(' '),
  danger: [
    'bg-error text-white',
    'shadow-sm hover:shadow-md',
    'hover:bg-error-dark',
    'focus:ring-2 focus:ring-error focus:ring-offset-2 focus:ring-offset-midnight',
  ].join(' '),
  'danger-outline': [
    'bg-transparent text-error border border-error',
    'hover:bg-error/10',
    'focus:ring-2 focus:ring-error focus:ring-offset-2 focus:ring-offset-midnight',
  ].join(' '),
  success: [
    'bg-success text-white',
    'shadow-sm hover:shadow-md',
    'hover:bg-success-dark',
    'focus:ring-2 focus:ring-success focus:ring-offset-2 focus:ring-offset-midnight',
  ].join(' '),
  accent: [
    'bg-gradient-to-r from-accent-dark via-accent to-accent-light text-white',
    'shadow-glow-accent hover:shadow-lg',
    'focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-midnight',
  ].join(' '),
};

const sizes = {
  sm: 'px-3 py-1.5 text-sm min-h-[32px]',
  md: 'px-5 py-2.5 text-base min-h-[44px]',
  lg: 'px-7 py-3.5 text-lg min-h-[52px]',
};

const iconSizes = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
};

const Button = forwardRef(function Button(
  {
    children,
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    fullWidth = false,
    iconLeft,
    iconRight,
    className,
    type = 'button',
    ...props
  },
  ref
) {
  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      className={cn(
        // Base styles
        'inline-flex items-center justify-center gap-2',
        'font-semibold rounded-lg',
        'transition-all duration-200 ease-out',
        'active:scale-[0.98]',
        'focus:outline-none',
        // Disabled state
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'disabled:hover:scale-100 disabled:active:scale-100',
        // Variant styles
        variants[variant],
        // Size styles
        sizes[size],
        // Full width
        fullWidth && 'w-full',
        className
      )}
      {...props}
    >
      {loading ? (
        <>
          <Spinner size={size === 'lg' ? 'md' : 'sm'} className="shrink-0" />
          <span>{children}</span>
        </>
      ) : (
        <>
          {iconLeft && (
            <span className={cn('shrink-0', iconSizes[size])}>{iconLeft}</span>
          )}
          <span>{children}</span>
          {iconRight && (
            <span className={cn('shrink-0', iconSizes[size])}>{iconRight}</span>
          )}
        </>
      )}
    </button>
  );
});

export default Button;

// Icon Button variant for icon-only buttons
export const IconButton = forwardRef(function IconButton(
  {
    children,
    variant = 'ghost',
    size = 'md',
    loading = false,
    disabled = false,
    label,
    className,
    ...props
  },
  ref
) {
  const iconButtonSizes = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
  };

  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-label={label}
      className={cn(
        'inline-flex items-center justify-center',
        'rounded-md',
        'transition-all duration-200 ease-out',
        'active:scale-[0.98]',
        'focus:outline-none',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        variants[variant],
        iconButtonSizes[size],
        className
      )}
      {...props}
    >
      {loading ? (
        <Spinner size={size === 'lg' ? 'md' : 'sm'} />
      ) : (
        <span className={iconSizes[size]}>{children}</span>
      )}
    </button>
  );
});
